import { useAlert, useConfirm } from '@genial/components';
import { Service } from '@genial/services';
import { format } from 'date-fns';
import { useCallback } from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { v4 } from 'uuid';

import { useToastCreator } from '../../../hooks/useToastCreator';
import { advisorIdToApisSelector, partnerIdToApisSelector } from '../../master/selector';

import { newWithdrawalCreatingAtom, newWithdrawalDownloadingReceiptAtom } from './atom';

export const useWithdrawalActions = () => {
  const advisorSelected = useRecoilValue(advisorIdToApisSelector);
  const partnerSelected = useRecoilValue(partnerIdToApisSelector);
  const [downloadingReceipt, setDownloadingReceipt] = useRecoilState(
    newWithdrawalDownloadingReceiptAtom
  );
  const setCreatingWithdrawal = useSetRecoilState(newWithdrawalCreatingAtom);
  const { alert, remove: removeAlert } = useAlert();
  const { confirm } = useConfirm();
  const { showToast } = useToastCreator(v4());

  const downloadReceipt = useCallback(
    async (id: number | string, accountId: number | string) => {
      const displayError = () => {
        setDownloadingReceipt(false);
        showToast(
          'error',
          'Não foi possível baixar o comprovante.',
          'Ocorreu um erro ao tentar baixar o comprovante. Por favor, tente novamente mais tarde ou entre em contato com time de middle pelo e-mail middleb2b@genial.com.vc.'
        );
      };

      if (downloadingReceipt) {
        return;
      }

      setDownloadingReceipt(true);
      alert({
        title: 'Aviso',
        description:
          'Esta solicitação pode levar alguns segundos. Assim que o download for \n' +
          'finalizado, o arquivo será baixado automaticamente.',
        icon: 'BackInTime',
        iconSize: 'xl',
        actionText: 'CONTINUAR',
      });

      try {
        const { data } = await Service.Finance.V1.getCashoutByReceiptAccountIdMovimentId({
          partnerId: partnerSelected,
          advisorId: advisorSelected,
          screenName: 'customerDetail',
          accountId: parseInt(accountId.toString(), 10),
          movimentId: id.toString(),
        });

        if (!data) {
          displayError();
          return;
        }

        const byteCharacters = atob(data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `comprovante-${id}.pdf`;
        a.click();
      } catch (_e) {
        displayError();
      } finally {
        setDownloadingReceipt(false);
        removeAlert();
      }
    },
    [advisorSelected, alert, downloadingReceipt, partnerSelected, setDownloadingReceipt]
  );

  const createWithdrawal = useCallback(
    async (
      accountId: string | number,
      amount: number,
      liquidationDate: Date,
      bankAccountId: string | number,
      token: string
    ) => {
      let created = false;
      setCreatingWithdrawal(true);

      try {
        await Service.Finance.V1.postCashoutExecute({
          screenName: 'customerDetail',
          cashoutExecuteRequest: {
            accountId: parseInt(accountId.toString(), 10),
            cashoutAmount: amount,
            liquidationDate,
            bankAccountId: parseInt(bankAccountId.toString(), 10),
            authenticationCode: token,
          },
        });

        created = true;
      } catch (_e) {
        created = false;
      } finally {
        setCreatingWithdrawal(false);
      }

      return created;
    },
    [setCreatingWithdrawal]
  );

  const isWithdrawalDateValid = useCallback(async (date: Date) => {
    try {
      const { data } = await Service.Finance.V1.getCashoutByValidateDate({
        validateDate: format(date, 'yyyy-MM-dd') as any,
        screenName: 'customerDetail',
      });

      return data.validateDate.isWorkingDay;
    } catch (_e) {
      return false;
    }
  }, []);

  const cancelWithdrawal = useCallback(
    (id: number | string, accountId: number | string) =>
      new Promise((resolve) => {
        confirm(
          {
            icon: 'Warning',
            iconSize: 'xl',
            title: 'Cancelamento de solicitação',
            description:
              'Ao continuar, você estará cancelando esta solicitação. Deseja continuar mesmo assim?',
            confirmColor: 'feedback.error.default',
            confirmText: 'Continuar',
          },
          async () => {
            try {
              const { data } = await Service.Finance.V1.postMovementCancelCashout({
                screenName: 'customerDetail',
                cancelCashOutMovementRequest: {
                  accountId: parseInt(accountId.toString(), 10),
                  movementIds: parseInt(id.toString(), 10),
                },
              });

              resolve(data.success);
            } catch (_e) {
              resolve(false);
            }
          }
        );
      }),
    [confirm]
  );

  return {
    downloadReceipt,
    cancelWithdrawal,
    createWithdrawal,
    isWithdrawalDateValid,
  };
};
